import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenants, createTenant, updateTenant, deleteTenant, generateTenantInviteLink } from '../services/tenantService';
import { usePermissions } from '../hooks/usePermissions';
import { Permissions } from '../constants/permissions';
import { Copy, Check, Building2, LogOut, PlusCircle, Edit, Trash2, Eye, X, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import useAuthStore from '../store/authStore';
import DataTable from './DataTable';
import PropelLogo from './PropelLogo';

export function OwnerTenantManagementPage() {
  const { hasPermission } = usePermissions();
  const { logout, user } = useAuthStore();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [form, setForm] = useState({ name: '', subdomain: '', organizationName: '', organizationEmail: '' });
  const [saving, setSaving] = useState(false);
  const [inviteLinks, setInviteLinks] = useState({});
  const [copiedLink, setCopiedLink] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteModalTenant, setInviteModalTenant] = useState(null);
  const [inviteLoadingTenantId, setInviteLoadingTenantId] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('ownerSidebarCollapsed');
    return stored ? stored === 'true' : true;
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    localStorage.setItem('ownerSidebarCollapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getTenants();
      setTenants(Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setForm({ name: '', subdomain: '', organizationName: '', organizationEmail: '' });
    setSelectedTenant(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setForm({
      name: tenant.name || '',
      subdomain: tenant.subdomain || '',
      organizationName: tenant.organizationName || '',
      organizationEmail: tenant.organizationEmail || '',
    });
    setSelectedTenant(tenant);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleViewTenant = (tenant) => {
    setForm({
      name: tenant.name || '',
      subdomain: tenant.subdomain || '',
      organizationName: tenant.organizationName || '',
      organizationEmail: tenant.organizationEmail || '',
    });
    setSelectedTenant(tenant);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      await deleteTenant(tenantId);
      await fetchTenants();
      // Clear invite link if it exists
      setInviteLinks((prev) => {
        const updated = { ...prev };
        delete updated[tenantId];
        return updated;
      });
    } catch (e) {
      setError(e?.message || 'Failed to delete tenant');
    }
  };

  const handleSaveTenant = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (modalMode === 'create') {
      const res = await createTenant({
          name: form.name.trim(),
          subdomain: form.subdomain.trim().toLowerCase(),
          organizationName: form.organizationName.trim() || undefined,
          organizationEmail: form.organizationEmail.trim() || undefined,
      });
      const tenant = res.data?.data ? res.data.data : (res.data || res);
      const inviteLink = res.data?.inviteLink || res.inviteLink || null;
      const inviteEmailSent = res?.data?.inviteEmailSent ?? res?.inviteEmailSent ?? null;
      const inviteEmailError = res?.data?.inviteEmailError || res?.inviteEmailError || null;
      
      if (inviteLink && tenant._id) {
        setInviteLinks((prev) => ({ ...prev, [tenant._id]: inviteLink }));
      }
      
        await fetchTenants();
        setIsModalOpen(false);
      setForm({ name: '', subdomain: '', organizationName: '', organizationEmail: '' });
        
        // Show invite link modal for new tenant
        if (inviteLink) {
          setInviteModalTenant({ ...tenant, inviteLink });
          setShowInviteModal(true);
        }

        if (inviteLink && inviteEmailSent === false) {
          setError(
            inviteEmailError
              ? `Tenant created, but invite email failed: ${inviteEmailError}. Please copy and send the invite link manually.`
              : 'Tenant created, but invite email failed. Please copy and send the invite link manually.'
          );
        }
      } else if (modalMode === 'edit' && selectedTenant) {
        await updateTenant(selectedTenant._id, {
          name: form.name.trim(),
          subdomain: form.subdomain.trim().toLowerCase(),
          organizationName: form.organizationName.trim() || undefined,
          organizationEmail: form.organizationEmail.trim() || undefined,
        });
        await fetchTenants();
        setIsModalOpen(false);
        setSelectedTenant(null);
      }
    } catch (e) {
      setError(e?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} tenant`);
    } finally {
      setSaving(false);
    }
  };

  const handleShowInviteLink = async (tenant) => {
    if (!tenant?.organizationEmail) {
      setError('Organization email is required to generate an invite link.');
      return;
    }

    try {
      setError(null);
      setInviteLoadingTenantId(tenant._id);

      const res = await generateTenantInviteLink(tenant._id);
      const inviteLink = res?.data?.inviteLink || res?.inviteLink || null;

      if (!inviteLink) {
        throw new Error('Invite link was not returned.');
      }

      setInviteLinks((prev) => ({ ...prev, [tenant._id]: inviteLink }));
      setInviteModalTenant({ ...tenant, inviteLink });
      setShowInviteModal(true);
    } catch (e) {
      setError(e?.message || 'Failed to generate invitation link');
    } finally {
      setInviteLoadingTenantId(null);
    }
  };

  const copyToClipboard = async (link, tenantId) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for HTTP contexts where Clipboard API is unavailable
        const textarea = document.createElement('textarea');
        textarea.value = link;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedLink(tenantId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard. Please select the text and copy manually.');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTenant(null);
    setModalMode('create');
    setForm({ name: '', subdomain: '', organizationName: '', organizationEmail: '' });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const workspaceLoginUrl = selectedTenant?.subdomain
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/${selectedTenant.subdomain}/login`
    : null;

  if (!hasPermission(Permissions.SYSTEM_TENANT_MANAGE)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access tenant management.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-300"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true,
    },
    {
      key: 'subdomain',
      label: 'Subdomain',
      accessor: 'subdomain',
      sortable: true,
      filterable: true,
      render: (value) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-gray-800 text-sm">/{value}</code>
      ),
    },
    {
      key: 'organizationName',
      label: 'Organization',
      accessor: 'organizationName',
      sortable: true,
      filterable: true,
      render: (value) => value || '-',
    },
    {
      key: 'organizationEmail',
      label: 'Email',
      accessor: 'organizationEmail',
      sortable: true,
      filterable: true,
      render: (value) => value || '-',
    },
    {
      key: 'createdAt',
      label: 'Created',
      accessor: 'createdAt',
      sortable: true,
      filterable: true,
      type: 'date',
      filterType: 'date',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      filterable: false,
      render: (_, tenant) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewTenant(tenant);
            }}
            className="text-primary-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
            title="View Tenant"
            type="button"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditTenant(tenant);
            }}
            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
            title="Edit Tenant"
            type="button"
          >
            <Edit className="h-4 w-4" />
          </button>
          {tenant.organizationEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShowInviteLink(tenant);
              }}
              disabled={inviteLoadingTenantId === tenant._id}
              className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50 transition-colors"
              title="Generate Invite Link"
              type="button"
            >
              {inviteLoadingTenantId === tenant._id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTenant(tenant._id);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
            title="Delete Tenant"
            type="button"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const handleSort = (key, direction) => {
    const sortedTenants = [...tenants].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setTenants(sortedTenants);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex admin-compact overflow-hidden">
      <div className={`${isSidebarCollapsed ? 'w-14' : 'w-60'} fixed h-screen z-40`}>
        {isSidebarCollapsed ? (
          <aside className="w-14 bg-white border-r border-gray-200 flex flex-col h-full">
            <div className="h-14 flex items-center justify-center border-b border-gray-200 relative">
              <div className="w-9 h-9 bg-white border border-primary-200 rounded-xl flex items-center justify-center shadow-sm">
                <PropelLogo className="w-5 h-5" />
              </div>
              <button
                type="button"
                onClick={() => setIsSidebarCollapsed(false)}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-50 w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-gray-700 hover:shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label="Expand sidebar"
              >
                <ChevronsRight className="w-3.5 h-3.5 mx-auto" />
              </button>
            </div>

            <div className="py-3 flex items-center justify-center border-b border-gray-100">
              <div
                className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center text-xs font-semibold text-primary-700"
                title={`${user?.name || 'Owner'} • ${user?.role || 'owner'}`}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'O'}
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-3 flex flex-col items-center gap-2">
              <div
                className="group relative flex items-center justify-center w-10 h-10 rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100"
                title="Tenant Management"
                aria-label="Tenant Management"
              >
                <Building2 className="h-5 w-5 text-primary-600" />
              </div>
            </nav>

            <div className="py-2 border-t border-gray-200 flex items-center justify-center">
              <button
                type="button"
                onClick={handleLogout}
                className="group relative flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </aside>
        ) : (
          <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
            <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 bg-white border border-primary-200 rounded-xl flex items-center justify-center shadow-sm">
                  <PropelLogo className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-sm font-bold text-gray-900">Propel</h1>
                  <p className="text-[10px] text-gray-500">Owner Console</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSidebarCollapsed(true)}
                className="w-8 h-8 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Collapse sidebar"
              >
                <ChevronsLeft className="w-4 h-4 mx-auto" />
              </button>
            </div>

            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-primary-700">
                    {user?.name?.charAt(0)?.toUpperCase() || 'O'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{user?.name || 'Owner'}</p>
                  <p className="text-[10px] text-gray-500 truncate capitalize">{user?.role || 'owner'}</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto p-3">
              <h3 className="px-2.5 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                Platform
              </h3>
              <div className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium bg-primary-50 text-primary-700">
                <Building2 className="h-4 w-4 flex-shrink-0 text-primary-600" />
                <span className="truncate">Tenant Management</span>
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-600 flex-shrink-0" />
              </div>
            </nav>

            <div className="p-3 border-t border-gray-200">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </aside>
        )}
      </div>

      <div className={`flex-1 flex flex-col ${isSidebarCollapsed ? 'ml-14' : 'ml-60'}`}>
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Tenant Management</h1>
                <p className="text-xs text-gray-500 mt-0.5">Create and manage tenant workspaces</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-2.5 py-1 bg-white text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors duration-150 flex items-center text-sm font-medium"
                type="button"
                title="Sign out"
              >
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto min-h-0">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <DataTable
            customActions={
              <button
                onClick={handleCreateTenant}
                className="px-2.5 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-150 flex items-center text-sm font-medium"
                type="button"
              >
                <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
                Create Tenant
              </button>
            }
            data={tenants}
            columns={columns}
            loading={loading}
            onSort={handleSort}
            currentPage={1}
            totalPages={1}
            totalCount={tenants.length}
            pageSize={tenants.length || 10}
            searchable={true}
            filterable={true}
            sortable={true}
            emptyMessage="No tenants found. Click 'Create Tenant' to add one."
            showPagination={false}
            showSearch={true}
            showFilters={true}
          />
        </main>
      </div>

      {/* Create/Edit/View Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="relative p-8 bg-white w-full max-w-md mx-auto rounded-lg shadow-lg">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={closeModal}
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-xl font-semibold mb-4">
              {modalMode === 'create' ? 'Create New Tenant' : modalMode === 'edit' ? 'Edit Tenant' : 'Tenant Details'}
            </h3>
            {modalMode === 'view' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                    <p className="mt-1 text-sm text-gray-900">{form.name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Tenant Slug</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">{form.subdomain || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Organization</label>
                    <p className="mt-1 text-sm text-gray-900">{form.organizationName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Organization Email</label>
                    <p className="mt-1 text-sm text-gray-900">{form.organizationEmail || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Status</label>
                    <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedTenant?.isActive !== false
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {selectedTenant?.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Tier</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{selectedTenant?.tier || 'free'}</p>
                  </div>
                </div>

                <div className="admin-filter-panel space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Tenant ID</label>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-sm text-gray-900 font-mono break-all">{selectedTenant?._id || '-'}</p>
                      {selectedTenant?._id && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(selectedTenant._id, 'tenant-id')}
                          className="p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors shrink-0"
                          title={copiedLink === 'tenant-id' ? 'Copied' : 'Copy tenant ID'}
                          aria-label={copiedLink === 'tenant-id' ? 'Copied' : 'Copy tenant ID'}
                        >
                          {copiedLink === 'tenant-id' ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {workspaceLoginUrl && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Workspace Login URL</label>
                      <div className="mt-1 flex items-start gap-2">
                        <p className="text-sm text-gray-900 font-mono break-all flex-1">{workspaceLoginUrl}</p>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(workspaceLoginUrl, 'workspace-url')}
                          className="p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors shrink-0"
                          title={copiedLink === 'workspace-url' ? 'Copied' : 'Copy workspace URL'}
                          aria-label={copiedLink === 'workspace-url' ? 'Copied' : 'Copy workspace URL'}
                        >
                          {copiedLink === 'workspace-url' ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Created</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDateTime(selectedTenant?.createdAt)}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Updated</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDateTime(selectedTenant?.updatedAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSaveTenant}>
                <div className="mb-4">
                  <label htmlFor="modal-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    id="modal-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    required
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="modal-subdomain" className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Slug *
                  </label>
                  <input
                    id="modal-subdomain"
                    name="subdomain"
                    type="text"
                    pattern="^[a-z0-9-]+$"
                    value={form.subdomain}
                    onChange={onChange}
                    readOnly={modalMode === 'edit'}
                    required
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    title="Lowercase letters, numbers, and hyphens only"
                  />
                  {modalMode === 'edit' && (
                    <p className="mt-1 text-xs text-gray-500">Subdomain cannot be changed after creation</p>
                  )}
                </div>
                <div className="mb-4">
                  <label htmlFor="modal-organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Name
                  </label>
                  <input
                    id="modal-organizationName"
                    name="organizationName"
                    type="text"
                    value={form.organizationName}
                    onChange={onChange}
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="modal-organizationEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Email
                  </label>
                  <input
                    id="modal-organizationEmail"
                    name="organizationEmail"
                    type="email"
                    value={form.organizationEmail}
                    onChange={onChange}
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                  >
                    {saving ? (modalMode === 'create' ? 'Creating...' : 'Saving...') : (modalMode === 'create' ? 'Create' : 'Save Changes')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Invite Link Modal */}
      {showInviteModal && inviteModalTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="relative p-8 bg-white w-full max-w-lg mx-auto rounded-lg shadow-lg">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => {
                setShowInviteModal(false);
                setInviteModalTenant(null);
              }}
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-xl font-semibold mb-4">Invitation Link</h3>
            <p className="text-sm text-gray-600 mb-4">
              Share this link with the admin user for <strong>{inviteModalTenant.name}</strong>:
            </p>
            <div className="mb-4">
              <div className="flex items-center justify-between gap-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                          <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-primary-800 mb-1">Invitation Link:</div>
                            <div className="text-sm text-primary-700 break-all font-mono bg-white px-2 py-1 rounded border border-primary-300">
                    {inviteModalTenant.inviteLink}
                            </div>
                          </div>
                          <button
                  onClick={() => copyToClipboard(inviteModalTenant.inviteLink, inviteModalTenant._id)}
                  className="flex-shrink-0 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-300 flex items-center gap-2 text-sm"
                            title="Copy to clipboard"
                  type="button"
                          >
                  {copiedLink === inviteModalTenant._id ? (
                              <>
                                <Check className="w-4 h-4" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
            </div>
            <div className="flex justify-end">
                        <a
                href={inviteModalTenant.inviteLink}
                          target="_blank"
                          rel="noopener noreferrer"
                className="text-sm text-primary-700 hover:text-primary-800 underline transition-colors duration-300"
                        >
                Open invitation link in new tab
                        </a>
                      </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteModalTenant(null);
                }}
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-300"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
                  </div>
        )}
    </div>
  );
}

export default OwnerTenantManagementPage;
